import React, { useEffect, useRef } from 'react';
import getName from '../../../utils/getName';
import { FastField, Field } from 'formik';
import { getIntl } from '../../../utils/intl';
import { FormikInputs } from '../../v2BaseComponents/Inputs';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import { CenteredFormRow, SplitFormRow } from '../../v2BaseComponents/Form';
import { ProviderSupportList } from './RenderWithProviders';

const intl = getIntl('ProviderFields', {
    provider: 'Provider',
    providerName: 'Provider Name',
    address: 'Address',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    zipCode: 'Zip Code',
    selectCareTeamProvider: 'Select provider from Care Team',
});

export type ProviderValues = {
    selectExistingProvider: boolean;
    providerCareTeamMemberId: string | null;
    providerName: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
};

export type ProviderFormValues = {
    resource: ProviderValues;
};

export type ProviderFieldsOwnProps = {
    value: {
        resource: ProviderValues;
    };
    providers: ProviderSupportList;
    isDisabled: boolean;
    isLoading: boolean;
    setFieldValue: (field: string, value: any) => void;
    /** Hide the free-text Provider Name input (a "With" select owns resource.providerName instead). */
    hideProviderNameInput?: boolean;
    /** Hide the "Select Care Team Provider" checkbox (an external control drives selectExistingProvider). */
    hideSelectToggle?: boolean;
    /** Hide the address inputs (org flows show them only after a Location is picked). */
    hideAddressInputs?: boolean;
};

const includePropInValues = (prop: keyof ProviderValues, values: ProviderValues) => values[prop] !== undefined;

export const getProviderSubmissionValues = (
    values: ProviderValues,
    providers: ProviderSupportList,
    omitAddress: boolean,
): ProviderValues => {
    if (values.selectExistingProvider || !values.providerCareTeamMemberId) {
        const provider = providers.find((_) => _.careTeamMemberId === values.providerCareTeamMemberId);
        if (!provider) {
            return {
                ...values,
                providerCareTeamMemberId: null,
            };
        }

        const returnValue: ProviderValues = {
            ...values,
            providerName: getName(provider.contactDetails),
        };

        if (!omitAddress) {
            returnValue.address1 = provider.contactDetails.address1 || null;
            returnValue.address2 = provider.contactDetails.address2 || null;
            returnValue.city = provider.contactDetails.city || null;
            returnValue.state = provider.contactDetails.state || null;
            returnValue.zipCode = provider.contactDetails.zipCode || null;
        }

        return returnValue;
    } else {
        return {
            ...values,
            providerCareTeamMemberId: null,
        };
    }
};

export const getProviderPropsInitialValues = (
    providers: ProviderSupportList,
    initialValues: ProviderFormValues | null,
    defaultValue: ProviderFormValues,
): ProviderFormValues => {
    if (!initialValues) {
        return defaultValue;
    }

    const defaultResourceProvider = providers.find(
        (_) => _.careTeamMemberId === initialValues.resource.providerCareTeamMemberId,
    );

    const noExplicitProviderFormValue: ProviderFormValues = {
        ...initialValues,
        resource: {
            ...initialValues.resource,
            providerCareTeamMemberId: null,
            selectExistingProvider: false,
        },
    };

    if (!defaultResourceProvider) {
        return noExplicitProviderFormValue;
    }

    const nameChanged = getName(defaultResourceProvider.contactDetails) !== initialValues.resource.providerName;
    if (nameChanged) {
        return noExplicitProviderFormValue;
    }

    return {
        ...initialValues,
        resource: {
            ...initialValues.resource,
            selectExistingProvider: true,
        },
    };
};

export type ProviderFieldsProps = ProviderFieldsOwnProps;

const ProviderFields: React.FC<ProviderFieldsProps> = ({
    value,
    providers,
    isDisabled,
    isLoading,
    setFieldValue,
    hideProviderNameInput,
    hideSelectToggle,
    hideAddressInputs,
}) => {
    const initialSelectExistingProvider = useRef(value.resource.selectExistingProvider);
    useEffect(() => {
        // reset provider fields if selectExistingProvider changes
        if (initialSelectExistingProvider.current !== value.resource.selectExistingProvider) {
            setFieldValue('resource.providerCareTeamMemberId', null);
            setFieldValue('resource.providerName', null);

            (['address1', 'address2', 'city', 'state', 'zipCode'] as const).forEach((_) => {
                if (includePropInValues(_, value.resource)) {
                    setFieldValue(`resource.${_}`, null);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.resource.selectExistingProvider]);

    return (
        <Stack vertical fullWidth space={contentStackSpacing}>
            {providers.length && !hideSelectToggle ? (
                <CenteredFormRow>
                    <FastField
                        name="resource.selectExistingProvider"
                        title={intl('selectCareTeamProvider')}
                        component={FormikInputs.Checkbox}
                        isDisabled={isDisabled || isLoading}
                        size={'sm'}
                    />
                </CenteredFormRow>
            ) : null}
            {value.resource.selectExistingProvider ? (
                <SplitFormRow>
                    <Field
                        component={FormikInputs.Select}
                        name="resource.providerCareTeamMemberId"
                        title={intl('provider')}
                        isDisabled={isDisabled || isLoading}
                        selectOptions={providers.map((_) => ({
                            label: getName(_.contactDetails) || _.contactDetails.company,
                            value: _.careTeamMemberId,
                            testID: getName(_.contactDetails) || _.contactDetails.company,
                        }))}
                    />
                </SplitFormRow>
            ) : (
                <>
                    <SplitFormRow>
                        {hideProviderNameInput ? null : (
                            <FastField
                                component={FormikInputs.Input}
                                name="resource.providerName"
                                title={intl('providerName')}
                                isDisabled={isDisabled || isLoading}
                            />
                        )}
                        {!hideAddressInputs && includePropInValues('address1', value.resource) ? (
                            <FastField
                                component={FormikInputs.Input}
                                name="resource.address1"
                                title={intl('address1')}
                                isDisabled={isDisabled || isLoading}
                            />
                        ) : null}
                    </SplitFormRow>
                    {!hideAddressInputs &&
                    (includePropInValues('address2', value.resource) || includePropInValues('city', value.resource)) ? (
                        <SplitFormRow>
                            {includePropInValues('address2', value.resource) ? (
                                <FastField
                                    component={FormikInputs.Input}
                                    name="resource.address2"
                                    title={intl('address2')}
                                    isDisabled={isDisabled || isLoading}
                                />
                            ) : null}
                            {includePropInValues('city', value.resource) ? (
                                <FastField
                                    component={FormikInputs.Input}
                                    name="resource.city"
                                    title={intl('city')}
                                    isDisabled={isDisabled || isLoading}
                                />
                            ) : null}
                        </SplitFormRow>
                    ) : null}
                    {!hideAddressInputs &&
                    (includePropInValues('state', value.resource) || includePropInValues('zipCode', value.resource)) ? (
                        <SplitFormRow>
                            {includePropInValues('state', value.resource) ? (
                                <FastField
                                    component={FormikInputs.Input}
                                    name="resource.state"
                                    title={intl('state')}
                                    isDisabled={isDisabled || isLoading}
                                />
                            ) : null}
                            {includePropInValues('zipCode', value.resource) ? (
                                <FastField
                                    component={FormikInputs.Input}
                                    name="resource.zipCode"
                                    title={intl('zipCode')}
                                    isDisabled={isDisabled || isLoading}
                                />
                            ) : null}
                        </SplitFormRow>
                    ) : null}
                </>
            )}
        </Stack>
    );
};

export default React.memo(ProviderFields);
